import React, { useMemo, useState, ReactNode } from 'react';
import { Stack, Box } from '@mui/material';
import { BestPractice } from './types';
import EditableBestPracticeCard from './EditableBestPracticeCard';
import SelectableBestPracticeCard from './SelectableBestPracticeCard';
import { SearchInput } from '../common/SearchInput';
import useSortWidget from '../common/useSortWidget';
import { colors, Typography, Button } from '@sweep-io/sweep-design';
import { AiAgent } from '../aiAgentCenter/aiAgentsConsts';

interface BestPracticesListProps {
  bestPractices: BestPractice[];
  isCustom: boolean;
  onIsPristineChange: (cardId: string, newIsPristine: boolean) => void;
  children: ReactNode;
  button?: ReactNode;
  agent?: AiAgent | null;
  isAgentCreationContext?: boolean;
  selectedBestPracticeIds?: string[];
  onBestPracticesChange?: (bestPracticeIds: string[]) => void;
}

const NAME_SORT = 'Name';
const DATE_MODIFIED_SORT = 'Date Modified';

const sortOptions = [NAME_SORT, DATE_MODIFIED_SORT];

const BestPracticesList: React.FC<BestPracticesListProps> = ({
  bestPractices,
  isCustom,
  onIsPristineChange,
  children,
  button,
  agent,
  isAgentCreationContext = false,
  selectedBestPracticeIds = [],
  onBestPracticesChange,
}) => {
  const [searchValue, setSearchValue] = useState('');

  const { SortWidget, sortBy } = useSortWidget({
    defaultValue: DATE_MODIFIED_SORT,
    sortOptions,
    size: 'small',
  });

  const filteredAndSortedBestPractices = useMemo(() => {
    const filteredList = bestPractices.filter((bestPractice) => {
      if (!searchValue.trim()) return true;

      const searchLower = searchValue.toLowerCase();
      return (
        bestPractice.name.toLowerCase().includes(searchLower) ||
        bestPractice.text.toLowerCase().includes(searchLower) ||
        bestPractice.applicableResourceType.toLowerCase().includes(searchLower)
      );
    });

    const sortedList = [...filteredList].sort((a, b) => {
      if (sortBy === NAME_SORT) {
        return a.name.localeCompare(b.name);
      } else if (sortBy === DATE_MODIFIED_SORT) {
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      }
      return 0;
    });

    return sortedList;
  }, [bestPractices, searchValue, sortBy]);

  const clearFilters = () => {
    setSearchValue('');
  };

  const noResults = filteredAndSortedBestPractices.length === 0 && searchValue.trim();

  return (
    <Stack gap={2} position="relative">
      <Stack
        sx={{
          backgroundColor: colors.white,
          position: 'sticky',
          top: 0,
          zIndex: 10,
          paddingBottom: 1,
        }}
      >
        <Stack
          direction="row"
          sx={{
            borderRadius: '8px',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              justifyContent: 'space-between',
              flex: 1,
            }}
          >
            <Box sx={{ flex: 1, maxWidth: '269px' }}>
              <SearchInput
                withFixedMagnifyingGlassIcon
                placeholder="Search"
                TextFieldProps={{
                  value: searchValue,
                  onChange: (event) => setSearchValue(event.target.value),
                }}
                onClearButtonClick={() => setSearchValue('')}
                size="small"
              />
            </Box>
            {SortWidget}
          </Box>
          {button ? button : null}
        </Stack>
      </Stack>

      {children}

      {noResults && (
        <Stack gap={1} p={3} alignItems="center">
          <Typography variant="h4" color={colors.grey[700]}>
            No results found for "{searchValue}"
          </Typography>
          <Button size="medium" variant="flat-blue" onClick={clearFilters}>
            Clear search
          </Button>
        </Stack>
      )}
      <Stack gap={2}>
        {filteredAndSortedBestPractices.map((bestPractice, index) =>
          isAgentCreationContext && onBestPracticesChange ? (
            <SelectableBestPracticeCard
              key={bestPractice.id}
              bestPractice={bestPractice}
              isCustom={isCustom}
              index={index}
              agent={agent}
              onIsPristineChange={(newIsPristine) =>
                onIsPristineChange(bestPractice.id, newIsPristine)
              }
              selectedBestPracticeIds={selectedBestPracticeIds}
              onBestPracticesChange={onBestPracticesChange}
            />
          ) : (
            <EditableBestPracticeCard
              key={bestPractice.id}
              bestPractice={bestPractice}
              isCustom={isCustom}
              index={index}
              agent={agent}
              onIsPristineChange={(newIsPristine) =>
                onIsPristineChange(bestPractice.id, newIsPristine)
              }
            />
          ),
        )}
      </Stack>
    </Stack>
  );
};

export default BestPracticesList;
